import { Device } from '@livecontrol/core-ui';
import type { ReactElement } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
/* eslint-disable-next-line import/no-internal-modules */
import { RAIL_COLLAPSED, RAIL_EXPANDED, useLayout } from './layout/context';
import { QuickSwitch } from './quick-switch';

/*
 * z-index 10: the rail overlays the content when it expands on hover, so it has
 * to clear anything the page stacks over the player. The video player marks its
 * surface `.full.z-3` and the event page puts a z-index 4 LIVE badge on top —
 * both sit in positioned-but-unstacked wrappers, so those values compete at the
 * root, not inside the player. Stay under the tooltip (30) and modal (200).
 */
const Wrapper = styled.aside<{ $expanded: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  height: 100vh;
  width: ${({ $expanded }): number => ($expanded ? RAIL_EXPANDED : RAIL_COLLAPSED)}px;
  padding: 22px 12px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  overflow: hidden;
  background: #080e1e;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  transition: width var(--transition), transform var(--transition);

  img {
    display: block;
    width: auto;
  }

  .logo-full {
    height: 34px;
  }

  .logo-small {
    height: 26px;
    max-width: 100%;
  }

  .x-${Device.MOBILE} & {
    width: ${RAIL_EXPANDED}px;
    box-shadow: 0 0 24px rgba(0, 0, 0, 0.5);
    transform: translateX(${({ $expanded }): string => ($expanded ? '0' : '-100%')});
  }
`;

const LogoRow = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $expanded }): string => ($expanded ? 'space-between' : 'center')};
  height: 34px;
  flex-shrink: 0;
`;

const PinButton = styled.button<{ $pinned: boolean }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  transition: all var(--transition);
  background: ${({ $pinned }): string => ($pinned ? 'rgba(78, 95, 253, 0.16)' : 'transparent')};
  color: ${({ $pinned }): string => ($pinned ? 'rgb(159, 176, 255)' : 'var(--white)')};
  border: ${({ $pinned }): string => ($pinned ? '1px solid rgba(120, 130, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)')};

  i {
    font-size: 14px;
    transition: transform 0.2s;
    transform: rotate(${({ $pinned }): string => ($pinned ? '0deg' : '40deg')});
  }

  &:hover,
  &:focus {
    outline: none;
    color: rgb(255, 255, 255);
    background: ${({ $pinned }): string => ($pinned ? 'rgba(78, 95, 253, 0.16)' : 'transparent')};
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const NavButton = styled.button<{ $expanded: boolean; $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 44px;
  width: ${({ $expanded }): string => ($expanded ? '100%' : '44px')};
  padding: ${({ $expanded }): string => ($expanded ? '0 12px' : '0')};
  justify-content: ${({ $expanded }): string => ($expanded ? 'flex-start' : 'center')};
  border: none;
  border-radius: 10px;
  white-space: nowrap;
  transition: all var(--transition);
  background: ${({ $active }): string => ($active ? 'rgba(78, 95, 253, 0.16)' : 'transparent')};
  border: ${({ $active }): string => ($active ? '1px solid rgba(120, 130, 255, 0.28)' : 'none')};
  color: var(--white);

  i {
    width: 20px;
    text-align: center;
    font-size: 18px;
    color: var(--white);
  }

  span {
    font-size: 14px;
    font-weight: 600;
  }

  &:hover,
  &:focus {
    outline: none;
    color: var(--white);
    background: ${({ $active }): string =>
      $active ? 'rgba(120, 130, 255, 0.18)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

export const Sidebar = (): ReactElement => {
  const { pinned, expanded, setPinned, setHovered, viewport } = useLayout();
  const history = useHistory();
  const { pathname } = useLocation();

  const isMobile = Device.isMobile(viewport);
  const onEventsRoute = pathname === '/events' || pathname.startsWith('/events/');

  return (
    <Wrapper
      $expanded={expanded}
      onMouseEnter={(): void => {
        if (!isMobile) {
          setHovered(true);
        }
      }}
      onMouseLeave={(): void => {
        if (!isMobile) {
          setHovered(false);
        }
      }}
    >
      <LogoRow $expanded={expanded}>
        {expanded ? (
          <>
            <img className='logo-full' src='/assets/entrance-light.svg' alt='LiveControl'/>
            <PinButton
              type='button'
              $pinned={pinned}
              aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
              onClick={(): void => {
                setPinned(!pinned);
              }}
            >
              <i className={pinned ? 'fas fa-thumbtack' : 'fal fa-thumbtack'} />
            </PinButton>
          </>
        ) : (
          <img className='logo-small' src='/assets/livecontrol-logo-small.png' alt='LiveControl' />
        )}
      </LogoRow>

      <Nav>
        <NavButton
          type='button'
          $expanded={expanded}
          $active={onEventsRoute}
          onClick={(): void => {
            history.push('/events');
          }}
        >
          <i className='fal fa-calendar' />
          {expanded && <span>All Events</span>}
        </NavButton>
      </Nav>

      <QuickSwitch expanded={expanded} />
    </Wrapper>
  );
};
